import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@dopomogai/ui';
import { useTranslation } from 'react-i18next';
import type { DocumentPageHeroConfig } from './types/docStructures';

interface DocumentPageLayoutProps {
  namespace: string;
  hero?: DocumentPageHeroConfig;
  children: ReactNode;
}

const variantToButtonVariant = (variant?: 'primary' | 'secondary' | 'ghost'): 'primary' | 'secondary' | 'ghost' =>
  variant === 'ghost' ? 'ghost' : variant === 'secondary' ? 'secondary' : 'primary';

export const DocumentPageLayout: React.FC<DocumentPageLayoutProps> = ({ namespace, hero, children }) => {
  const { t } = useTranslation(namespace);

  return (
    <div className="py-8 md:py-12">
      {hero && (
        <section className="mb-10 md:mb-14 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl shadow-xl px-6 py-12 md:px-12 md:py-16">
          {hero.eyebrowKey && (
            <p className="text-xs uppercase tracking-[0.3em] text-slate-300 mb-3">
              {t(hero.eyebrowKey)}
            </p>
          )}

          <div className="space-y-4 max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-extrabold">{t(hero.titleKey)}</h1>
            {hero.subtitleKey && <p className="text-slate-200 text-base md:text-lg">{t(hero.subtitleKey)}</p>}
          </div>

          {(hero.primaryCta || hero.secondaryCta) && (
            <div className="mt-8 flex flex-wrap gap-3">
              {hero.primaryCta && (
                <Button
                  asChild
                  size="lg"
                  variant={variantToButtonVariant(hero.primaryCta.variant)}
                  className="shadow-lg shadow-emerald-500/30"
                >
                  <Link to={t(hero.primaryCta.pathKey)}>{t(hero.primaryCta.textKey)}</Link>
                </Button>
              )}
              {hero.secondaryCta && (
                <Button
                  asChild
                  size="lg"
                  variant={variantToButtonVariant(hero.secondaryCta.variant ?? 'secondary')}
                  className="shadow-md shadow-slate-900/20 bg-white/10 hover:bg-white/20 border-white/30"
                >
                  <Link to={t(hero.secondaryCta.pathKey)}>{t(hero.secondaryCta.textKey)}</Link>
                </Button>
              )}
            </div>
          )}
        </section>
      )}

      <div className="max-w-3xl mx-auto space-y-12 md:space-y-16">{children}</div>
    </div>
  );
};